import axios from "axios";
import { Case, Party, Document, Event, Deadline, Evidence } from "../types";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://comarca.5bc.us/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ==================== CASE API ====================

export const caseAPI = {
  getAll: async (status?: string) => {
    const params = status ? { status } : {};
    const response = await api.get<Case[]>("/cases/", { params });
    return response.data;
  },

  getById: async (caseId: number) => {
    const response = await api.get<Case>(`/cases/${caseId}`);
    return response.data;
  },

  create: async (caseData: Partial<Case>) => {
    const response = await api.post<Case>("/cases/", caseData);
    return response.data;
  },

  update: async (caseId: number, caseData: Partial<Case>) => {
    const response = await api.put<Case>(`/cases/${caseId}`, caseData);
    return response.data;
  },
};

export const db1995API = {
  list: async (
    caseCode?: string,
    limit: number = 200000,
    caseId?: number
  ) => {
    const response = await api.get("/db1995", {
      params: {
        ...(caseCode ? { case_code: caseCode } : {}),
        limit,
        ...(caseId ? { case_id: caseId } : {}),
      },
    });
    return response.data;
  },
  update: async (
    rowId: number,
    payload: {
      index_name?: string;
      reference_title?: string;
      verified?: boolean;
      public_date?: string | null;
    }
  ) => {
    const response = await api.patch(`/db1995/${rowId}`, payload);
    return response.data;
  },
  getCatalog: async () => {
    const response = await api.get("/db1995/catalog");
    return response.data;
  },
  getCatalogSection: async (section: string, offset: number = 0, limit: number = 100) => {
    const response = await api.get("/db1995/catalog-section", {
      params: { section, offset, limit },
    });
    return response.data;
  },
  search: async (q: string, limit: number = 1000) => {
    const response = await api.get("/db1995/search", {
      params: { q, limit },
    });
    return response.data;
  },
  getPlainText: async (rowId: number, refresh: boolean = false) => {
    const response = await api.get(`/db1995/${rowId}/plain-text`, {
      params: refresh ? { refresh: true } : {},
      responseType: "text",
    });
    return response.data as string;
  },
  getByDocument: async (documentId: number) => {
    const response = await api.get(`/db1995/by-document/${documentId}`);
    return response.data;
  },
};

// ==================== DOCUMENT API ====================

export const documentAPI = {
  upload: async (file: File, caseId: number, documentType: string) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("case_id", caseId.toString());
    formData.append("document_type", documentType);

    const response = await api.post("/documents/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Document[]>(`/documents/${caseId}`);
    return response.data;
  },

  update: async (documentId: number, data: { event_id?: number; document_type?: string }) => {
    const response = await api.patch<Document>(`/documents/${documentId}`, data);
    return response.data;
  },

  delete: async (documentId: number) => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  },

  getPlainText: async (documentId: number, refresh: boolean = false) => {
    const response = await api.get(`/documents/${documentId}/plain-text`, {
      params: refresh ? { refresh: true } : {},
      responseType: "text",
    });
    return response.data as string;
  },

  /** Replace a stored TIFF with a PDF (same document row). */
  convertTiffToPdf: async (documentId: number) => {
    const response = await api.post(
      `/documents/${documentId}/convert-tiff-to-pdf`
    );
    return response.data;
  },

  /** Detect orientation and rewrite PDF upright/horizontal on disk.
   *  Default: orientation only (fast). Pass reextract=true to also re-OCR. */
  uprightPdf: async (documentId: number, reextract = false) => {
    const response = await api.post(
      `/documents/${documentId}/upright-pdf`,
      null,
      {
        // Orientation-only stays light (150); re-OCR uses HD (400+)
        params: { reextract, dpi: reextract ? 400 : 150 },
        timeout: 180000,
      }
    );
    return response.data as {
      document_id: number;
      title: string;
      changed: boolean;
      rotations: number[];
      pages: number;
      message: string;
      matricula?: {
        matricula_id: number;
        matricula?: string;
        superficie_m2?: string;
        timbre_fecha_hora?: string;
      };
    };
  },

  analyze: async (documentId: number) => {
    const response = await api.post(`/documents/${documentId}/analyze`);
    return response.data;
  },

  getSavedAnalysis: async (documentId: number) => {
    const response = await api.get(`/documents/${documentId}/analysis`);
    return response.data;
  },

  analyzeCombined: async (documentIds: number[]) => {
    const response = await api.post(
      "/documents/analyze-combined",
      { document_ids: documentIds },
      {
        // Combined analysis can take many minutes (many PDFs + large model response).
        timeout: 900000,
      }
    );
    return response.data;
  },

  generateBoughtPropertiesReport: async (caseId: number) => {
    const response = await api.post(`/cases/${caseId}/bought-properties/generate`, null, {
      timeout: 120000,
    });
    return response.data;
  },

  downloadBoughtPropertiesXlsxUrl: (caseId: number) =>
    `${API_BASE_URL}/cases/${caseId}/bought-properties/export.xlsx`,

  informeLegalHtmlUrl: (caseId: number, documentId?: number) =>
    `${API_BASE_URL}/cases/${caseId}/informe-legal.html${
      documentId ? `?document_id=${documentId}` : ""
    }`,

  informePrecautelaDocxUrl: (caseId: number) =>
    `${API_BASE_URL}/cases/${caseId}/informe-precautela.docx`,

  informeMonitorioDocxUrl: (caseId: number, documentId?: number) =>
    `${API_BASE_URL}/cases/${caseId}/informe-monitorio.docx${
      documentId ? `?document_id=${documentId}` : ""
    }`,

  informeAutoVistaDocxUrl: (caseId: number, documentId?: number) =>
    `${API_BASE_URL}/cases/${caseId}/informe-auto-vista.docx${
      documentId ? `?document_id=${documentId}` : ""
    }`,

  copyToDb1995: async (documentId: number) => {
    const response = await api.post(`/documents/${documentId}/copy-to-db1995`);
    return response.data as {
      message: string;
      id: number;
      original_file_name: string;
      file_type?: string;
      plain_text?: string;
      plain_text_chars?: number;
      content_url?: string;
      download_url?: string;
    };
  },

  copyCasePdfsToDb1995: async (caseId: number) => {
    const response = await api.post(`/cases/${caseId}/copy-pdfs-to-db1995`);
    return response.data;
  },
};

// ==================== AI ANALYSIS API ====================

export const aiAPI = {
  getPrecedents: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/precedents`);
    return response.data;
  },

  getRiskAnalysis: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/risk-analysis`);
    return response.data;
  },

  getTimeline: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/timeline`);
    return response.data;
  },

  getOmissions: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/omissions`);
    return response.data;
  },

  getPatterns: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/patterns`);
    return response.data;
  },

  extractAuthoritiesToMac: async (caseId: number) => {
    const response = await api.post(`/cases/${caseId}/extract-authorities-to-mac`);
    return response.data;
  },

  getCombinedAnalysis: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/combined-analysis`);
    return response.data;
  },

  getOcrExportUrl: (caseId: number) =>
    `${API_BASE_URL}/cases/${caseId}/ocr-export`,
};

// ==================== MATRICULAS / PLANILLA ====================

export const matriculaAPI = {
  list: async (caseId?: number) => {
    const response = await api.get("/matriculas", {
      params: {
        ...(caseId != null ? { case_id: caseId } : {}),
        limit: 2000,
        include_asientos: true,
      },
    });
    return response.data;
  },

  listByCase: async (caseId: number) => {
    const response = await api.get(`/cases/${caseId}/matriculas`);
    return response.data;
  },

  extractCase: async (caseId: number, onlyFolioReal = true) => {
    const response = await api.post(`/cases/${caseId}/matriculas/extract`, {
      only_folio_real: onlyFolioReal,
      dry_run: false,
    });
    return response.data;
  },

  /** High-DPI re-OCR of the PDF + re-extract matrícula fields (can take 1–3 min). */
  reocrHd: async (
    matriculaId: number,
    opts?: { dpi?: number; max_pages?: number }
  ) => {
    const response = await api.post(
      `/matriculas/${matriculaId}/reocr-hd`,
      {
        dpi: opts?.dpi ?? 450,
        max_pages: opts?.max_pages ?? 2,
        max_seconds: 180,
      },
      { timeout: 300000 }
    );
    return response.data as {
      matricula: any;
      dpi: number;
      max_pages: number;
      ocr_chars: number;
      matricula_antes?: string;
      matricula_despues?: string;
      mensaje: string;
    };
  },

  getXlsxUrl: (caseId?: number) =>
    caseId != null
      ? `${API_BASE_URL}/cases/${caseId}/matriculas/export.xlsx`
      : `${API_BASE_URL}/matriculas/export.xlsx`,
};

// ==================== EVENT API ====================

export const eventAPI = {
  create: async (eventData: Partial<Event>) => {
    const response = await api.post<Event>("/events/", eventData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Event[]>(`/events/${caseId}`);
    return response.data;
  },
};

// ==================== DEADLINE API ====================

export const deadlineAPI = {
  create: async (deadlineData: Partial<Deadline>) => {
    const response = await api.post<Deadline>("/deadlines/", deadlineData);
    return response.data;
  },

  getUpcoming: async (days: number = 7) => {
    const response = await api.get(`/deadlines/upcoming?days=${days}`);
    return response.data;
  },
};

// ==================== PARTY API ====================

export const partyAPI = {
  create: async (partyData: Partial<Party>) => {
    const response = await api.post<Party>("/parties/", partyData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Party[]>(`/parties/${caseId}`);
    return response.data;
  },
};

// ==================== EVIDENCE API ====================

export const evidenceAPI = {
  create: async (evidenceData: Partial<Evidence>) => {
    const response = await api.post<Evidence>("/evidence/", evidenceData);
    return response.data;
  },

  getByCaseId: async (caseId: number) => {
    const response = await api.get<Evidence[]>(`/evidence/${caseId}`);
    return response.data;
  },

  getDownloadUrl: (evidenceId: number) =>
    `${process.env.REACT_APP_API_URL || "https://comarca.5bc.us/api"}/evidence/by-id/${evidenceId}/download`,

  uploadFile: async (evidenceId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(
      `/evidence/by-id/${evidenceId}/upload`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return response.data;
  },

  analyze: async (evidenceId: number) => {
    const response = await api.post(`/evidence/by-id/${evidenceId}/analyze`);
    return response.data;
  },
};

export default api;
